import { createFeatureSelector, createSelector } from '@ngrx/store';
import { State, userFeatureKey } from './user.reducer';
import { Contact, UserRoleEnum } from '@qvs/core';
import { environment } from '#environment';
import { isEmpty, sortBy } from 'lodash-es';
import { selectCrmFavoriteClientIds } from '@qvs/core/preference';

export const selectUserFeature = createFeatureSelector<State>(userFeatureKey);

export const selectUserRole = createSelector(selectUserFeature, (state) => state.userProfile?.role);

export const selectUserProfile = createSelector(selectUserFeature, (state) => state.userProfile);

export const selectUserName = createSelector(selectUserProfile, (state) =>
  `${state?.person?.name ?? ''} ${state?.person?.lastName ?? ''}`.trim()
);

export const hasCrmRole = createSelector(selectUserRole, (state) => state === UserRoleEnum.CLIENT_CRM);

export const selectUserCapabilities = createSelector(selectUserProfile, (state) => state.capabilities);

export const selectIsForcedPickClientForRMEnabled = createSelector(
  (_state) => environment.featureFlags.forcedPickClientForRM,
  hasCrmRole,
  (forcedPickClientForRM, isCrm) => forcedPickClientForRM && isCrm
);

export const selectCrmContact = createSelector(selectUserFeature, (state) =>
  state.userProfile?.role === UserRoleEnum.CLIENT ? state.userProfile.contacts?.[0] : null
);

export const selectCrmContactImage = createSelector(selectCrmContact, (contact) => contact?.picture);

export const selectCrmMyContactInfo = createSelector(selectUserFeature, (state) => state.userProfile?.person);

export const selectCrmMyContacts = createSelector(selectUserFeature, (state) => {
  if (state.userProfile?.role === UserRoleEnum.CLIENT_CRM) {
    return state.userProfile.contacts ?? [];
  }
  return [];
});

export const selectUserProfileLoadingStatus = createSelector(selectUserFeature, (state) => state.loadingStatus);

export const selectCurrentClient = createSelector(selectUserFeature, (state) => {
  if (!state.userProfile?.contacts) {
    return null;
  }
  return state.userProfile.contacts.find((contact) => contact.contractId === state.crm?.currentClient?.contractId);
});

export const selectRelevantContact = createSelector(
  selectUserRole,
  selectCrmContact,
  selectCurrentClient,
  (userRole, crmContact, currentClient) => (userRole === UserRoleEnum.CLIENT ? crmContact : currentClient)
);

export const selectFavoriteClientIds = createSelector(selectCrmFavoriteClientIds, (favoriteClientIds) => favoriteClientIds || []);

/**
 * Sort clients by favorite status and then by last name A-Z. Favorite clients will be at the top of the list.
 */
export const selectSortedClients = createSelector(selectCrmMyContacts, selectFavoriteClientIds, (clients, favoriteClientIds) => {
  if (isEmpty(clients)) {
    return [];
  }

  const [favorites, others] = clients.reduce<[Contact[], Contact[]]>(
    ([fav, nonFav], client) =>
      favoriteClientIds.includes(client.staffId) ? [[...fav, client], nonFav] : [fav, [...nonFav, client]],
    [[], []]
  );

  return [...sortBy(favorites, ['lastName']), ...sortBy(others, ['lastName'])];
});

export const selectFirstSortedClient = createSelector(selectSortedClients, (sortedClients) => sortedClients?.[0] || null);
